import { useCallback, useEffect, useRef, useState } from 'react';

interface Options {
  /** localStorage key the chosen height is persisted under. */
  storageKey: string;
  /** Smallest allowed height (≈ one row of thumbnails). */
  min: number;
  /** Height used when nothing is stored. */
  defaultHeight: number;
  /** Fraction of the available column height the bar may occupy at most. */
  maxFraction?: number;
}

interface Result {
  height: number;
  /** Spread onto the drag handle element. */
  handleProps: {
    onPointerDown: (e: React.PointerEvent) => void;
    role: 'separator';
    'aria-orientation': 'horizontal';
  };
}

function readStored(key: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(key);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) && n > 0 ? n : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Height of a bottom bar the user can resize by dragging a handle on its top
 * edge (drag up = taller). Clamps between `min` and `maxFraction` of the parent
 * column, and persists the chosen height to localStorage.
 */
export function useResizableBarHeight({
  storageKey,
  min,
  defaultHeight,
  maxFraction = 0.75,
}: Options): Result {
  const [height, setHeight] = useState(() =>
    readStored(storageKey, defaultHeight)
  );
  const heightRef = useRef(height);
  const dragState = useRef<{
    startY: number;
    startHeight: number;
    max: number;
  }>({ startY: 0, startHeight: 0, max: Infinity });

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      const { startY, startHeight, max } = dragState.current;
      // Dragging up (smaller clientY) grows the bar.
      const next = Math.max(
        min,
        Math.min(max, startHeight + (startY - e.clientY))
      );
      heightRef.current = next;
      setHeight(next);
    },
    [min]
  );

  const endDrag = useCallback(() => {
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    try {
      localStorage.setItem(storageKey, String(Math.round(heightRef.current)));
    } catch {
      // ignore persistence failures
    }
  }, [onPointerMove, storageKey]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      // Max = a fraction of the containing column so the viewer above always
      // keeps room. The handle's parent is the left (viewer) column.
      const column = (e.currentTarget as HTMLElement).parentElement;
      const columnHeight = column?.clientHeight ?? window.innerHeight;
      dragState.current = {
        startY: e.clientY,
        startHeight: heightRef.current,
        max: Math.max(min, columnHeight * maxFraction),
      };
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
    },
    [min, maxFraction, onPointerMove, endDrag]
  );

  // Detach any in-flight drag listeners if the component unmounts mid-drag, and
  // undo the body styles. Refs hold the latest handlers so the unmount-only
  // effect doesn't re-subscribe on every render.
  const onPointerMoveRef = useRef(onPointerMove);
  const endDragRef = useRef(endDrag);
  useEffect(() => {
    onPointerMoveRef.current = onPointerMove;
    endDragRef.current = endDrag;
  });
  useEffect(
    () => () => {
      window.removeEventListener('pointermove', onPointerMoveRef.current);
      window.removeEventListener('pointerup', endDragRef.current);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    },
    []
  );

  return {
    height,
    handleProps: {
      onPointerDown,
      role: 'separator',
      'aria-orientation': 'horizontal',
    },
  };
}
